/**
 * The global login — one page, one job.
 *
 * A person arrives at `app.[staging.]hr.linkco.jo` knowing their email and
 * their password, and nothing else. No company field: asking someone to recall
 * their employer's URL slug is asking them to remember an implementation
 * detail. `POST /auth/login` with no `tenantSlug` resolves the company from
 * the credentials and names it back, which `performLogin` records for the app
 * that boots next (see `sessionTenantSlugStore`).
 *
 * The audience is the only open question, and `audienceProbe` answers it by
 * trying — see that file for why `admin` goes first. The retry happens inside
 * `<LoginForm>`'s own submit, so the whole two-request sequence is ONE pending
 * button, never a second form.
 *
 * Branding is neutral (`passive: true`): on this host the company is unknown
 * until the password is checked, so there is nothing to brand with. It is the
 * same mode Link Admin's platform login uses.
 *
 * Landing is a hard `window.location.assign` because the destination is a
 * different bundle on the same origin — it re-bootstraps under its own
 * audience and re-authenticates from the credential just minted.
 */
import { useRef, useState, type JSX } from "react";
import { useNavigate } from "@tanstack/react-router";
import { AuthLayout, LoginForm, OtpForm, TotpForm, useAuthBranding } from "@linkhr/auth/pages";
import { Alert } from "@linkhr/design-system";
import { configureAuthNetwork, type Audience, type AuthSnapshot } from "@linkhr/auth";
import { useTranslation } from "@linkhr/i18n";
import { env } from "./env";
import { FIRST_AUDIENCE, nextAudienceAfterRefusal, nextAudienceForIdToken } from "./audienceProbe";
import { landingAfterSignIn } from "./resumeSession";

interface OtpState {
  otpSessionToken: string;
  ttl: number;
  channel: "email" | "sms";
  maskedEmail: string;
}

interface TotpState {
  totpSessionToken: string;
  ttl: number;
}

export function GlobalLoginPage(): JSX.Element {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const branding = useAuthBranding("", { passive: true });
  const [otpState, setOtpState] = useState<OtpState | null>(null);
  const [totpState, setTotpState] = useState<TotpState | null>(null);
  const [notice, setNotice] = useState<"totp_session_expired" | null>(null);

  // Which audience the credentials were accepted for. A ref, not state: the
  // MFA steps read it and a re-render would only repaint the same form.
  const acceptedAudience = useRef<Audience>(FIRST_AUDIENCE);

  /**
   * Retarget every audience-scoped side effect before the retry: the refresh
   * path, the `X-Refresh-Audience` hint, the cross-tab channel and the
   * host-scoped tenant markers all key off this. Skipping it files a perfectly
   * good employee session under `admin`, where the employee app cannot see it.
   */
  const switchAudience = (aud: Audience): void => {
    acceptedAudience.current = aud;
    configureAuthNetwork({ baseUrl: env.VITE_API_BASE_URL, aud });
  };

  // A temporary password from HR lands on that app's password change; a
  // sign-in that started on the consent page goes back to it.
  const land = (snap: AuthSnapshot): void => {
    const redirect = new URLSearchParams(window.location.search).get("redirect");
    window.location.assign(
      landingAfterSignIn(acceptedAudience.current, snap.mustChangePassword === true, redirect),
    );
  };

  const step: "credentials" | "otp" | "totp" =
    totpState !== null ? "totp" : otpState !== null ? "otp" : "credentials";

  const headerTitle =
    step === "credentials"
      ? t("login.title_neutral")
      : step === "otp"
        ? t("otp.title")
        : t("totp.title");
  const headerSubtitle =
    step === "credentials"
      ? t("login.subtitle_global")
      : step === "otp"
        ? t("otp.subtitle", { maskedEmail: otpState?.maskedEmail ?? "" })
        : t("totp.subtitle");

  return (
    <AuthLayout branding={branding}>
      <header className="flex flex-col gap-xs">
        <h1 id="login-heading" className="text-default text-2xl">
          {headerTitle}
        </h1>
        <p className="text-muted text-sm">{headerSubtitle}</p>
      </header>

      {step === "credentials" && notice === "totp_session_expired" && (
        <Alert variant="warning" description={t("totp.session_expired_notice")} />
      )}

      {step === "totp" && totpState !== null ? (
        <TotpForm
          totpSessionToken={totpState.totpSessionToken}
          apiBaseUrl={env.VITE_API_BASE_URL}
          audience={acceptedAudience.current}
          hideHeading
          headingId="login-heading"
          onBack={() => {
            setTotpState(null);
          }}
          onSessionExpired={() => {
            setTotpState(null);
            setNotice("totp_session_expired");
          }}
          onSuccess={land}
        />
      ) : step === "otp" && otpState !== null ? (
        <OtpForm
          otpSessionToken={otpState.otpSessionToken}
          ttl={otpState.ttl}
          channel={otpState.channel}
          maskedEmail={otpState.maskedEmail}
          apiBaseUrl={env.VITE_API_BASE_URL}
          audience={acceptedAudience.current}
          hideHeading
          headingId="login-heading"
          onBack={() => {
            setOtpState(null);
          }}
          onSuccess={land}
        />
      ) : (
        <LoginForm
          // No company field, and none sent: the backend resolves it from the
          // credentials and names it back on the response.
          tenantSlug=""
          apiBaseUrl={env.VITE_API_BASE_URL}
          audience={FIRST_AUDIENCE}
          onForgotPassword={() => {
            void navigate({ to: "/forgot", search: true });
          }}
          googleClientId={env.VITE_GOOGLE_CLIENT_ID}
          microsoftClientId={env.VITE_MICROSOFT_CLIENT_ID}
          hideHeading
          // Every request — first try or retry, password, Google or Microsoft — retargets
          // the audience-scoped plumbing to the audience it is sent with.
          onAttempt={switchAudience}
          onAudienceRefused={nextAudienceAfterRefusal}
          onIdTokenAudienceRefused={nextAudienceForIdToken}
          onSuccess={land}
          onOtpRequired={(otp) => {
            setNotice(null);
            setOtpState(otp);
          }}
          onTotpRequired={(totp) => {
            setNotice(null);
            setTotpState(totp);
          }}
        />
      )}
    </AuthLayout>
  );
}
